/** @jsx jsx */

import React, { useMemo } from "react";
import Recaptcha from "react-google-recaptcha";
import { Form, Formik, ErrorMessage } from "formik";
import { Typography, message, Button, Icon, InputNumber } from "antd";
import { jsx } from "@emotion/core";

import AuthLayout from "../../components/authLayout";
import CustomButton from "../../components/button";
import { FormInput, FormRadioButton } from "../../components/formHelpers";
import useRedirect from "../../helperHooks/useRedirect";
import useLoginIssue from "../../hooks/auth/useLoginIssue";

import {
  loginIssueConstants as CONSTANTS,
  loginIssueValidationSchema as ValidationSchema,
  IssueType,
  issueTypeOptions
} from "./LoginIssueConstants";
import { MOBILE_COUNTRY_CODE } from "../../Constants";
import { AppSettings } from "../../AppSettings";
import { LoginIssuePayload } from "../../services/authService/AuthService.type";
import { loginIssueStyle, backToLoginStyle } from "./LoginIssueStyles";

type InitialValues = LoginIssuePayload & {
  isVerified: boolean;
};

const { Text } = Typography;

const LoginIssue: React.FC = () => {
  const recaptchaDevKey = AppSettings.server.recaptchaKey;
  const {
    reportLoginIssue,
    ReportLoginIssueState: {
      phoneNumber: initialPhoneNumber,
      fromLogin,
      loading
    }
  } = useLoginIssue();
  const redirect = useRedirect();

  const initialValues: InitialValues = useMemo(
    () => ({
      issue: fromLogin
        ? IssueType.noOtpReceivedLogin
        : IssueType.noOtpReceivedRegistration,
      isVerified: false,
      phoneNumber: initialPhoneNumber,
      isLogin: fromLogin
    }),
    [initialPhoneNumber, fromLogin]
  );

  const sendLoginIssueReport = (values: InitialValues) => {
    const { isVerified, issue, phoneNumber, isLogin } = values;
    if (isVerified) {
      reportLoginIssue({
        data: {
          issue,
          phoneNumber,
          isLogin
        },
        onSuccess: responseMessage => message.success(responseMessage)
      });
    }
  };
  const renderForm = (
    <AuthLayout>
      <Button
        className="login-back-container"
        type="link"
        css={backToLoginStyle}
        onClick={() => {
          redirect(routes =>
            fromLogin ? routes.login.new : routes.registration.new
          );
        }}
      >
        <Icon type="left" />
        {CONSTANTS.labels.goBack(fromLogin)}
      </Button>
      <div css={loginIssueStyle} className="mx-auto">
        <Formik
          initialValues={initialValues}
          validationSchema={ValidationSchema}
          onSubmit={sendLoginIssueReport}
          validateOnChange
        >
          {({ setFieldValue }) => (
            <Form>
              <div className="form-input-container">
                <FormInput
                  autoFocus
                  name="phoneNumber"
                  label={CONSTANTS.labels.phoneNumber(fromLogin)}
                  placeholder={CONSTANTS.placeholders.phoneNumber}
                  mandatory
                >
                  <InputNumber
                    className="mr-3 country-code"
                    value={MOBILE_COUNTRY_CODE}
                    formatter={value => `+${value}`}
                    disabled
                  />
                </FormInput>
                <div className="captcha-container d-flex justify-content-center mt-4">
                  <Recaptcha
                    sitekey={recaptchaDevKey}
                    onChange={() => {
                      setFieldValue("isVerified", true);
                    }}
                  />
                </div>
                <ErrorMessage name="isVerified">
                  {errorMessage => (
                    <Text className="error">{errorMessage}</Text>
                  )}
                </ErrorMessage>
                <FormRadioButton
                  name="issue"
                  className="mt-4"
                  label={CONSTANTS.labels.issue}
                  options={issueTypeOptions(fromLogin)}
                  mandatory
                />
                <CustomButton
                  htmlType="submit"
                  containerDivClassName="justify-content-center mt-4"
                  type="primary"
                  loading={loading}
                >
                  {CONSTANTS.buttons.submit}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
  return <div>{renderForm}</div>;
};
export default LoginIssue;
